import { runSliced } from './mainThreadScheduler';

export default class TaskScheduleValidator {
  constructor(originalNestedTasks, newNestedTasks, locations = []) {
    this.originalNestedTasks = originalNestedTasks;
    this.newNestedTasks = newNestedTasks;
    this.originalTasksMap = new Map();
    this.newTasksMap = new Map();
    this.changes = new Map();
    this.deletedTasks = [];
    this.locationsMap = new Map(locations.map(loc => [loc.id?.toString(), loc.title || loc.id]));
    this.buildTaskMap(originalNestedTasks, this.originalTasksMap);
    this.buildTaskMap(newNestedTasks, this.newTasksMap);
  }

  /**
   * Async so the per-task diff can yield to the event loop between time
   * slices on large schedules. onProgress({ stage, done, total }) reports
   * how many tasks have been compared.
   */
  async validate({ onProgress } = {}) {
    await this.compareNestedTasksChunked(onProgress);
    this.detectDeletedTasks();
    return {
      tasks: this.reinsertDeletedTasks(),
      changes: this.getChangesAsObject()
    };
  }

  // Same DFS pre-order walk as compareNestedTasks, but driven by an explicit
  // stack so it can be time-sliced.
  async compareNestedTasksChunked(onProgress) {
    const stack = [];
    for (let i = this.newNestedTasks.length - 1; i >= 0; i--) {
      stack.push(this.newNestedTasks[i]);
    }
    const total = this.newTasksMap.size;
    let done = 0;

    await runSliced(() => {
      if (stack.length === 0) return false;
      const updatedTask = stack.pop();
      const originalTask = this.originalTasksMap.get(updatedTask.id);

      if (!originalTask) {
        this.recordChange(updatedTask.id, 'new', ['New task added']);
      } else {
        this.compareTasks(originalTask, updatedTask);
      }

      if (updatedTask.children) {
        for (let i = updatedTask.children.length - 1; i >= 0; i--) {
          stack.push(updatedTask.children[i]);
        }
      }
      done += 1;
      return stack.length > 0;
    }, {
      onYield: onProgress
        ? () => onProgress({ stage: 'comparing', done, total })
        : undefined
    });
    if (onProgress) onProgress({ stage: 'comparing', done, total });
  }

  getChangesAsObject() {
    const changesObj = {};
    this.changes.forEach((changeInfo, taskId) => {
      changesObj[taskId] = changeInfo;
    });
    return changesObj;
  }

  buildTaskMap(tasks, map) {
    tasks.forEach(task => {
      map.set(task.id, task);
      if (task.children && task.children.length > 0) {
        this.buildTaskMap(task.children, map);
      }
    });
  }

  compareNestedTasks(tasks) {
    tasks.forEach(updatedTask => {
      const originalTask = this.originalTasksMap.get(updatedTask.id);

      if (!originalTask) {
        this.recordChange(updatedTask.id, 'new', ['New task added']);
      } else {
        this.compareTasks(originalTask, updatedTask);
      }

      if (updatedTask.children && updatedTask.children.length > 0) {
        this.compareNestedTasks(updatedTask.children);
      }
    });
  }

  detectDeletedTasks() {
    this.originalTasksMap.forEach((task, taskId) => {
      if (!this.newTasksMap.has(taskId)) {
        this.recordChange(taskId, 'deleted', ['Task deleted']);
        this.deletedTasks.push({
          ...task,
          wbs: [...task.wbs],
          children: [],
          markedForDeletion: true
        });
      }
    });
  }

  /**
   * Build the display tree: the new tree with every deleted original put back
   * under its parent, flagged markedForDeletion so the gantt can show it red.
   *
   * Returns a NEW tree and leaves the input untouched. The tree handed in is
   * also the commit payload on the P6 XML path (SpreadsheetProcessor returns
   * one array as both nestedTasks and displayTasks), and the commit endpoint
   * upserts every task it receives — splicing the deleted originals into that
   * shared tree would send them straight back to the API, so the "deleted"
   * task would survive the sync.
   * @returns {Array} Display tree (cloned nodes; task fields are shared)
   */
  reinsertDeletedTasks() {
    // Shallow-clone every node and its children array so reinsertion never
    // touches the caller's tree. Task fields are shared (the diff is done).
    const displayById = new Map();
    const cloneTree = (tasks) => tasks.map(task => {
      const cloned = { ...task };
      if (Array.isArray(task.children)) {
        cloned.children = cloneTree(task.children);
      }
      displayById.set(cloned.id, cloned);
      return cloned;
    });
    const displayTasks = cloneTree(this.newNestedTasks);

    // Registering each reinserted task keeps a deleted child findable under
    // its deleted parent (deletedTasks is in original-tree pre-order, parents
    // first).
    this.deletedTasks.forEach(deletedTask => {
      const parentTask = displayById.get(deletedTask.parentTaskId);
      if (parentTask) {
        if (!parentTask.children) {
          parentTask.children = [];
        }
        parentTask.children.push(deletedTask);
      } else {
        displayTasks.push(deletedTask);
      }
      displayById.set(deletedTask.id, deletedTask);
    });

    return displayTasks;
  }

  /**
   * Normalize a DurationInfo object to days for comparison purposes.
   * Also supports legacy d.HH:MM:SS strings.
   */
  calculateDurationInDays(duration) {
    if (!duration) return null;

    // DurationInfo object: { value, unit } (current) or { duration, durationUnit } (legacy)
    if (typeof duration === 'object' && (duration.value !== undefined || duration.duration !== undefined)) {
      const val = duration.value !== undefined ? duration.value : duration.duration;
      switch (duration.unit || duration.durationUnit) {
        case 'minute': return val / (24 * 60);
        case 'hour': return val / 24;
        case 'day': return val;
        case 'week': return val * 7;
        case 'month': return val * 30;
        case 'year': return val * 365;
        default: return val;
      }
    }

    // Legacy d.HH:MM:SS string
    if (typeof duration === 'string') {
      const legacyPattern = /^(\d+)\.(\d{2}):(\d{2}):(\d{2})$/;
      const match = duration.match(legacyPattern);
      if (match) {
        return parseInt(match[1]) +
          (parseInt(match[2]) / 24) +
          (parseInt(match[3]) / (24 * 60)) +
          (parseInt(match[4]) / (24 * 60 * 60));
      }

      // Shorthand format
      const shorthandPattern = /^(\d+\.?\d*)\s*(mo|m|h|d|w|y)?$/i;
      const sMatch = duration.match(shorthandPattern);
      if (sMatch) {
        const num = parseFloat(sMatch[1]);
        const suffix = (sMatch[2] || 'd').toLowerCase();
        const multipliers = { 'm': 1 / (24 * 60), 'h': 1 / 24, 'd': 1, 'w': 7, 'mo': 30, 'y': 365 };
        return num * (multipliers[suffix] || 1);
      }
    }

    return null;
  }

  checkDeletedTasks(tasks) {
    const updatedTaskIds = new Set(this.collectTaskIds(tasks));
    
    this.originalTasksMap.forEach((originalTask, id) => {
      if (!updatedTaskIds.has(id)) {
        this.recordChange(id, 'deleted', ['Task deleted']);
      }
    });
  }

  collectTaskIds(tasks) {
    return tasks.flatMap(task => 
      [task.id, ...(task.children ? this.collectTaskIds(task.children) : [])]
    );
  }

  resolveLocationNames(locationIds) {
    if (!locationIds || locationIds.length === 0) return 'None';
    return locationIds
      .map(id => this.locationsMap.get(id?.toString()) || id)
      .join(', ');
  }

  // Fields the API does not persist/return, so diffing them against a fresh
  // import would flag every task: type/sequenceNumber are parser-internal,
  // rowIndex is spreadsheet-validation metadata, wbs is handled explicitly.
  static NON_PERSISTED_KEYS = ['children', 'key', 'id', 'rowIndex', 'type', 'sequenceNumber', 'wbs'];

  compareTasks(original, updated) {
    const changes = [];
    const subtaskChanges = [];

    if (updated.wbs !== undefined && !TaskScheduleValidator.deepEqual(original.wbs, updated.wbs)) {
      changes.push(`WBS changed from ${JSON.stringify(original.wbs)} to ${JSON.stringify(updated.wbs)}`);
    }

    for (const key in updated) {
      if (TaskScheduleValidator.NON_PERSISTED_KEYS.includes(key)) continue;

      if (key === 'subtasks') {
        this.compareSubtasks(original.subtasks, updated.subtasks, subtaskChanges);
      } else if (key === 'timeRanges') {
        this.compareTimeRanges(original.timeRanges, updated.timeRanges, changes);
      } else if (key === 'viewableLocationIds') {
        if (!TaskScheduleValidator.deepEqual(original[key], updated[key])) {
          const fromNames = this.resolveLocationNames(original[key]);
          const toNames = this.resolveLocationNames(updated[key]);
          changes.push(`Viewable Locations changed from: ${fromNames} to: ${toNames}`);
        }
      } else if (key === 'childTaskIds' || key === 'prerequisiteTaskIds') {
        // Membership matters, ordering doesn't.
        const originalSet = new Set(original[key]);
        const updatedSet = new Set(updated[key]);

        if (originalSet.size !== updatedSet.size ||
            ![...originalSet].every(id => updatedSet.has(id))) {
          changes.push(`${key} changed from ${JSON.stringify(original[key])} to ${JSON.stringify(updated[key])}`);
        }
      } else if (!this.areValuesEqual(original[key], updated[key])) {
        changes.push(`${key} changed from ${JSON.stringify(original[key])} to ${JSON.stringify(updated[key])}`);
      }
    }

    if (changes.length > 0 || subtaskChanges.length > 0) {
      this.recordChange(updated.id, 'updated', changes, subtaskChanges);
    }
  }

  /**
   * Equality that treats '' / null / undefined as the same "empty" value —
   * the API returns "" where imports produce null (e.g. a root task's
   * parentTaskId), and neither is a real change.
   */
  areValuesEqual(a, b) {
    const normalize = (value) => (value === '' || value === undefined) ? null : value;
    return TaskScheduleValidator.deepEqual(normalize(a), normalize(b));
  }

  /**
   * Structural equality with JSON semantics — Dates compare as their ISO
   * string, undefined object values count as absent, undefined array slots
   * count as null — but without serializing both sides on every field, which
   * dominated diff time on large schedules. Unlike JSON.stringify it ignores
   * object key order, which is never a real change.
   */
  static deepEqual(a, b) {
    if (a === b) return true;
    if (a instanceof Date) a = a.toISOString();
    if (b instanceof Date) b = b.toISOString();
    if (typeof a === 'number' && !Number.isFinite(a)) a = null;
    if (typeof b === 'number' && !Number.isFinite(b)) b = null;
    if (a === b) return true;
    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;

    const aIsArray = Array.isArray(a);
    if (aIsArray !== Array.isArray(b)) return false;
    if (aIsArray) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!TaskScheduleValidator.deepEqual(a[i] ?? null, b[i] ?? null)) return false;
      }
      return true;
    }

    const aKeys = Object.keys(a).filter(key => a[key] !== undefined);
    const bKeys = Object.keys(b).filter(key => b[key] !== undefined);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
      if (b[key] === undefined) return false;
      if (!TaskScheduleValidator.deepEqual(a[key], b[key])) return false;
    }
    return true;
  }

  areDatesEqual(a, b) {
    return this.getUTCTimestamp(a) === this.getUTCTimestamp(b);
  }

  /**
   * Semantic timeRanges comparison. The stored (API) representation differs
   * from a freshly imported one in ways that are not changes: date strings in
   * other formats/offsets for the same instant, durations re-expressed by the
   * backend in humanized units with renormalized weightings. So ranges are
   * paired by template and compared field-by-field on meaning, not JSON.
   */
  compareTimeRanges(originalTimeRanges, updatedTimeRanges, changes) {
    const originalByTemplate = new Map(
      (originalTimeRanges || []).map(timeRange => [timeRange.timeRangeTemplateId, timeRange])
    );

    (updatedTimeRanges || []).forEach(updatedRange => {
      const label = updatedRange.timeRangeTemplateTitle || updatedRange.timeRangeTemplateId;
      const originalRange = originalByTemplate.get(updatedRange.timeRangeTemplateId);
      if (!originalRange) {
        changes.push(`Time range ${label} added`);
        return;
      }
      originalByTemplate.delete(updatedRange.timeRangeTemplateId);

      const startsEqual = this.areDatesEqual(originalRange.startDate, updatedRange.startDate);
      if (!startsEqual) {
        changes.push(`Time range ${label}: start changed from ${originalRange.startDate} to ${updatedRange.startDate}`);
      }

      // endDate is derived at commit time (start + duration + calendar), so
      // only compare when both sides carry one.
      const bothEnds = originalRange.endDate != null && updatedRange.endDate != null;
      const endsEqual = bothEnds && this.areDatesEqual(originalRange.endDate, updatedRange.endDate);
      if (bothEnds && !endsEqual) {
        changes.push(`Time range ${label}: end changed from ${originalRange.endDate} to ${updatedRange.endDate}`);
      }

      // A duration is fully determined by start + end (+ calendar), and the
      // backend re-expresses committed durations in humanized units the
      // client can't reproduce — so matching endpoints mean a matching
      // duration whatever representation each side uses. weighting is
      // server-normalized and never compared.
      if (!(startsEqual && endsEqual)) {
        if (!this.areDurationsEqual(originalRange.duration, updatedRange.duration)) {
          changes.push(`Time range ${label}: duration changed from ${JSON.stringify(originalRange.duration)} to ${JSON.stringify(updatedRange.duration)}`);
        }
      }

      ['calendarId', 'calendarVersionReference', 'progress'].forEach(field => {
        if (!this.areValuesEqual(originalRange[field], updatedRange[field])) {
          changes.push(`Time range ${label}: ${field} changed from ${JSON.stringify(originalRange[field])} to ${JSON.stringify(updatedRange[field])}`);
        }
      });
    });

    originalByTemplate.forEach(originalRange => {
      changes.push(`Time range ${originalRange.timeRangeTemplateTitle || originalRange.timeRangeTemplateId} removed`);
    });
  }

  getUTCTimestamp(dateString) {
    if (!dateString) return null;
    const date = new Date(dateString);
    return Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds()
    );
  }

  areDurationsEqual(duration1, duration2) {
    const days1 = this.calculateDurationInDays(duration1);
    const days2 = this.calculateDurationInDays(duration2);
    if (days1 === null || days2 === null) return days1 === days2;
    return Math.abs(days1 - days2) < 0.0000001;
  }

  // Subtask fields that are not in the API's Subtask model (SubTaskDTO carries
  // no createdBy*/parentTaskId). Older client builds stamped them on every
  // subtask; diffing them against a GET flagged every task that has a subtask.
  static SUBTASK_NON_PERSISTED_KEYS = ['createdByUserId', 'createdByTeamId', 'parentTaskId'];

  compareSubtasks(originalSubtasks, updatedSubtasks, subtaskChanges) {
    for (let i = 0; i < updatedSubtasks.length; i++) {
      const updatedSubtask = updatedSubtasks[i];
      const originalSubtask = originalSubtasks[i];

      if (!originalSubtask) {
        subtaskChanges.push(`New subtask added: ${updatedSubtask.title}`);
      } else {
        for (const key in updatedSubtask) {
          if (TaskScheduleValidator.SUBTASK_NON_PERSISTED_KEYS.includes(key)) continue;
          // ''/null/undefined are one "empty" value here too: the API returns
          // "" for an unset leadByTeamId/itemId, an import may carry null.
          if (!this.areValuesEqual(originalSubtask[key], updatedSubtask[key])) {
            subtaskChanges.push(`Subtask ${i + 1}: ${key} changed from ${JSON.stringify(originalSubtask[key])} to ${JSON.stringify(updatedSubtask[key])}`);
          }
        }
      }
    }

    if (originalSubtasks.length > updatedSubtasks.length) {
      for (let i = updatedSubtasks.length; i < originalSubtasks.length; i++) {
        subtaskChanges.push(`Subtask deleted: ${originalSubtasks[i].title}`);
      }
    }
  }

  recordChange(taskId, changeType, changes, subtaskChanges = []) {
    const changeInfo = {
      changeType,
      changes,
      ...(subtaskChanges.length > 0 && { subtaskChanges })
    };
    this.changes.set(taskId, changeInfo);
  }
}